// src/services/dimensions.ts
import { api } from "@/lib/apiClient";
import type { GroCrudScope } from "@/lib/groCrudScope";
import {
  createCompanyGroDimension,
  getCompanyGroTemplates,
  setCompanyGroDimensionStatus,
  updateCompanyGroDimension,
} from "@/services/modelImports";
import type { Dimension, DimensionListItem, DimensionUpsertIn } from "@/types/dimensions";

const isCompany = (scope: GroCrudScope) => scope === "company_model";

export async function listDimensions(
  params?: { q?: string; risk_theme?: string; is_active?: boolean },
  scope: GroCrudScope = "client",
) {
  if (isCompany(scope)) {
    const data = await getCompanyGroTemplates();
    const q = params?.q?.trim().toLocaleLowerCase("pt-BR") ?? "";
    return data.dimensions.filter((item) => {
      if (params?.risk_theme && item.risk_theme !== params.risk_theme) return false;
      if (typeof params?.is_active === "boolean" && item.is_active !== params.is_active) return false;
      if (!q) return true;
      return [item.code, item.name, item.description]
        .filter(Boolean)
        .some((value) => String(value).toLocaleLowerCase("pt-BR").includes(q));
    }) as DimensionListItem[];
  }

  return api.get<DimensionListItem[]>("/dimensions", {
    params: {
      q: params?.q || undefined,
      risk_theme: params?.risk_theme || undefined,
      is_active: typeof params?.is_active === "boolean" ? params.is_active : undefined,
    },
  });
}

export async function getDimension(id: string, scope: GroCrudScope = "client") {
  if (isCompany(scope)) {
    const data = await getCompanyGroTemplates();
    const item = data.dimensions.find((dimension) => dimension.id === id);
    if (!item) throw new Error("Dimensão modelo não encontrada.");
    return item as Dimension;
  }
  return api.get<Dimension>(`/dimensions/${id}`);
}

export async function createDimension(payload: DimensionUpsertIn, scope: GroCrudScope = "client") {
  if (isCompany(scope)) return createCompanyGroDimension(payload);
  return api.post<Dimension>("/dimensions", payload);
}

export async function updateDimension(
  id: string,
  payload: DimensionUpsertIn,
  scope: GroCrudScope = "client",
) {
  if (isCompany(scope)) return updateCompanyGroDimension(id, payload);
  return api.put<Dimension>(`/dimensions/${id}`, payload);
}

export async function setDimensionStatus(
  id: string,
  is_active: boolean,
  scope: GroCrudScope = "client",
) {
  if (isCompany(scope)) return setCompanyGroDimensionStatus(id, is_active);
  return api.patch<Dimension>(`/dimensions/${id}/status`, { is_active });
}

export async function deleteDimension(id: string, scope: GroCrudScope = "client") {
  if (isCompany(scope)) return api.delete<void>(`/company-gro-templates/dimensions/${id}`);
  return api.delete<void>(`/dimensions/${id}`);
}
